import * as smartpromise from '@push.rocks/smartpromise';

/**
 * delay something, works like setTimeout
 * @param timeInMillisecondArg
 * @param passOnArg
 */
export const delayFor = async <T>(
  timeInMillisecondArg: number,
  passOnArg?: T,
  unrefedArg = false
) => {
  const timeout = new Timeout<null>(timeInMillisecondArg, null, unrefedArg);
  await timeout.promise;
  return passOnArg;
};

/**
 * delay for a random time
 */
export const delayForRandom = async <T>(
  timeMinInMillisecondArg: number,
  timeMaxInMillisecondArg: number,
  passOnArg?: T,
  unrefedArg = false
) => {
  await delayFor(
    Math.random() * (timeMaxInMillisecondArg - timeMinInMillisecondArg) + timeMinInMillisecondArg,
    null,
    unrefedArg
  );
  return passOnArg;
};

/**
 * The largest delay setTimeout accepts. Longer delays fire after 1 ms in Node.js and
 * browsers, so Timeout waits for them in chunks of at most this size.
 */
const maxTimerDelayMs = 2_147_483_647;

export class Timeout<T> {
  promise: Promise<T>;
  private _deferred: smartpromise.Deferred<T>;
  private _timeout: ReturnType<typeof setTimeout>;
  private _cancelled: boolean = false;
  private _unrefed: boolean = false;
  private _passOn: T | undefined;

  private timeoutInMillis: number;
  private started: number;

  constructor(timeInMillisecondArg: number, passOn?: T, unrefedArg = false) {
    this.timeoutInMillis = timeInMillisecondArg;
    this._passOn = passOn;
    this._deferred = smartpromise.defer<T>();
    this.promise = this._deferred.promise;
    this._timeout = this.startChunk(timeInMillisecondArg);
    this.started = Date.now();
    if (unrefedArg) {
      this.makeUnrefed();
    }
  }

  /**
   * starts the one live timer for the next chunk of the remaining delay
   */
  private startChunk(remainingMillisArg: number): ReturnType<typeof setTimeout> {
    const chunkMillis = Math.min(remainingMillisArg, maxTimerDelayMs);
    const chunkTimeout = setTimeout(() => {
      if (this._cancelled) {
        return;
      }
      const remainingMillis = remainingMillisArg - chunkMillis;
      if (remainingMillis > 0) {
        this._timeout = this.startChunk(remainingMillis);
      } else {
        this._deferred.resolve(this._passOn);
      }
    }, chunkMillis);
    if (this._unrefed) {
      Timeout.unrefTimer(chunkTimeout);
    }
    return chunkTimeout;
  }

  private static unrefTimer(timeoutArg: ReturnType<typeof setTimeout>) {
    const timeoutWithUnref = timeoutArg as ReturnType<typeof setTimeout> & {
      unref?: () => void;
    };
    timeoutWithUnref.unref?.();
  }

  /**
   * unreffing a timeout causes the node process to not wait for completion before exit;
   * it applies to every remaining chunk of a long delay
   */
  public makeUnrefed() {
    this._unrefed = true;
    Timeout.unrefTimer(this._timeout);
  }

  /**
   * cancels the timer
   */
  public cancel() {
    this._cancelled = true;
    clearTimeout(this._timeout);
  }

  public getTimeLeft() {
    const result = this.started + this.timeoutInMillis - Date.now();
    return result > 0 ? result : 0;
  }
}
